<template>
  <div class="feedback-management-container">
    <div class="page-header">
      <div class="header-left">
        <h2>{{ $t('feedback.title') || '用户反馈管理' }}</h2>
        <div class="mobile-controls" v-if="isMobile">
          <el-button 
            size="small" 
            :type="filtersCollapsed ? 'info' : 'primary'" 
            @click="toggleFilters"
            :icon="Filter"
          >
            {{ filtersCollapsed ? '展开筛选' : '收起筛选' }}
          </el-button>
          <el-button 
            size="small" 
            :type="showMobileCards ? 'primary' : 'info'" 
            @click="toggleMobileView"
          >
            {{ showMobileCards ? '表格模式' : '卡片模式' }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 搜索区域 -->
    <div class="search-bar" v-show="!filtersCollapsed || !isMobile">
      <el-form :inline="!isMobile" :model="searchForm" class="search-form" :class="{ 'mobile-form': isMobile }">
        <el-form-item :label="$t('feedback.searchLabel') || '搜索内容'">
          <el-input
            v-model="searchForm.search"
            :placeholder="$t('feedback.searchPlaceholder') || '搜索反馈内容、用户名或邮箱'"
            clearable
            :style="{ width: isMobile ? '100%' : '280px' }"
            @clear="handleSearch"
            @keyup.enter="handleSearch"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item :label="$t('feedback.type') || '反馈类型'">
          <el-select
            v-model="searchForm.feedback_type"
            :placeholder="$t('feedback.typePlaceholder') || '请选择反馈类型'"
            clearable
            :style="{ width: isMobile ? '100%' : '160px' }"
            @change="handleSearch"
          >
            <el-option :label="$t('feedback.typeRequirement') || '需求建议'" value="requirement_suggestion">
              <span style="display: flex; align-items: center;">
                <el-icon style="margin-right: 8px; color: #409EFF;"><HelpFilled /></el-icon>
                {{ $t('feedback.typeRequirement') || '需求建议' }}
              </span>
            </el-option>
            <el-option :label="$t('feedback.typeException') || '功能异常'" value="function_exception">
              <span style="display: flex; align-items: center;">
                <el-icon style="margin-right: 8px; color: #F56C6C;"><Warning /></el-icon>
                {{ $t('feedback.typeException') || '功能异常' }}
              </span>
            </el-option>
            <el-option :label="$t('feedback.options.complaint') || '问题投诉'" value="complaint">
              <span style="display: flex; align-items: center;">
                <el-icon style="margin-right: 8px; color: #F56C6C;"><Warning /></el-icon>
                {{ $t('feedback.options.complaint') || '问题投诉' }}
              </span>
            </el-option>
            <el-option :label="$t('feedback.typeOther') || '其他'" value="other">
              <span style="display: flex; align-items: center;">
                <el-icon style="margin-right: 8px; color: #909399;"><More /></el-icon>
                {{ $t('feedback.typeOther') || '其他' }}
              </span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('feedback.status') || '处理状态'">
          <el-select
            v-model="searchForm.status"
            :placeholder="$t('feedback.statusPlaceholder') || '请选择处理状态'"
            clearable
            :style="{ width: isMobile ? '100%' : '130px' }"
            @change="handleSearch"
          >
            <el-option :label="$t('feedback.statusPending') || '待处理'" value="pending" />
            <el-option :label="$t('feedback.statusProcessing') || '处理中'" value="processing" />
            <el-option :label="$t('feedback.statusCompleted') || '已完成'" value="completed" />
            <el-option :label="$t('feedback.statusClosed') || '已关闭'" value="closed" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('feedback.submitTimeRange') || '提交时间'">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="datetimerange"
            :start-placeholder="$t('feedback.startTime') || '开始时间'"
            :end-placeholder="$t('feedback.endTime') || '结束时间'"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            clearable
            :style="{ width: isMobile ? '100%' : '380px' }"
            @change="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            {{ $t('feedback.search') || '搜索' }}
          </el-button>
          <el-button @click="handleReset">
            <el-icon><Refresh /></el-icon>
            {{ $t('feedback.reset') || '重置' }}
          </el-button>
          <el-button type="success" @click="handleExport" :loading="exportLoading">
            <el-icon><Download /></el-icon>
            {{ $t('feedback.export') || '导出CSV' }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>



    <!-- 移动端卡片模式 -->
    <div v-if="showMobileCards && isMobile" class="mobile-cards-container">
      <div v-loading="loading" class="cards-loading-wrapper">
        <div v-for="item in feedbackList" :key="item.id" class="feedback-card">
          <div class="card-header">
            <div class="feedback-info">
              <h3>{{ item.user?.username || '未知用户' }}</h3>
              <p class="email">{{ item.user?.email || '-' }}</p>
              <span class="feedback-id">ID: {{ item.id }}</span>
            </div>
            <div class="status-type">
              <el-tag :type="getStatusColor(item.status)" size="small">
                {{ getStatusText(item.status) }}
              </el-tag>
              <el-tag :type="getFeedbackTypeColor(item.feedback_type)" size="small" style="margin-top: 4px;">
                <el-icon style="margin-right: 4px;">
                  <component :is="getFeedbackTypeIcon(item.feedback_type)" />
                </el-icon>
                {{ getFeedbackTypeText(item.feedback_type) }}
              </el-tag>
            </div>
          </div>
          
          <div class="card-content">
            <div class="feedback-content-section">
              <h4>反馈内容</h4>
              <div class="content-preview">
                {{ item.content }}
              </div>
            </div>
            
            <div class="feedback-meta">
              <div class="meta-item">
                <span class="label">联系方式</span>
                <span class="value">{{ item.contact_info || '-' }}</span>
              </div>
              <div class="meta-item">
                <span class="label">提交时间</span>
                <span class="value">{{ formatDateTime(item.created_at) }}</span>
              </div>
            </div>
          </div>
          
          <div class="card-actions">
            <el-button size="small" type="primary" plain @click="handleViewDetail(item)">
              <el-icon><View /></el-icon>
              查看详情
            </el-button>
            <el-button size="small" type="success" plain @click="handleEdit(item)">
              <el-icon><Edit /></el-icon>
              编辑
            </el-button>
          </div>
        </div>
      </div>
    </div>

      <!-- 反馈列表表格 -->
      <el-table
        v-if="!showMobileCards || !isMobile"
        v-loading="loading"
        :data="feedbackList"
        style="width: 100%"
        @sort-change="handleSortChange"
        :empty-text="$t('feedback.empty') || '暂无反馈数据'"
      >
        <el-table-column prop="id" label="ID" width="80" sortable />
        <el-table-column prop="user.username" :label="$t('feedback.username') || '用户名'" width="120">
          <template #default="scope">
            {{ scope.row.user?.username || '未知用户' }}
          </template>
        </el-table-column>
        <el-table-column prop="user.email" :label="$t('feedback.email') || '用户邮箱'" width="180">
          <template #default="scope">
            {{ scope.row.user?.email || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="feedback_type" :label="$t('feedback.type') || '反馈类型'" width="160">
          <template #default="scope">
            <el-tag :type="getFeedbackTypeColor(scope.row.feedback_type)" style="display: flex; align-items: center; justify-content: center; width: fit-content;">
              <el-icon style="margin-right: 4px;">
                <component :is="getFeedbackTypeIcon(scope.row.feedback_type)" />
              </el-icon>
              {{ getFeedbackTypeText(scope.row.feedback_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="content" :label="$t('feedback.content') || '反馈内容'" min-width="260">
          <template #default="scope">
            <div class="feedback-content">
              {{ scope.row.content }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="contact_info" :label="$t('feedback.contact') || '联系方式'" width="150">
          <template #default="scope">
            {{ scope.row.contact_info || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="status" :label="$t('feedback.status') || '处理状态'" width="100">
          <template #default="scope">
            <el-tag :type="getStatusColor(scope.row.status)">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" :label="$t('feedback.createdAt') || '提交时间'" width="180" sortable>
          <template #default="scope">
            {{ formatDateTime(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('feedback.operation') || '操作'" width="180" fixed="right">
          <template #default="scope">
            <el-button
              type="primary"
              size="small"
              @click="handleViewDetail(scope.row)"
            >
              {{ $t('feedback.view') || '查看详情' }}
            </el-button>
            <el-button
              type="success"
              size="small"
              @click="handleEdit(scope.row)"
            >
              {{ $t('feedback.edit') || '编辑' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        v-if="pagination.total > 0"
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.size"
        :page-sizes="[10, 20, 50, 100]"
        :small="false"
        :disabled="loading"
        :background="true"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 反馈详情对话框 -->
    <el-dialog
      v-model="detailVisible"
      :title="$t('feedback.detail') || '反馈详情'"
      :width="isMobile ? '95%' : '60%'"
      :fullscreen="isMobile"
      :before-close="handleCloseDetail"
    >
      <div v-if="selectedFeedback" class="feedback-detail">
        <el-descriptions :column="isMobile ? 1 : 2" border>
          <el-descriptions-item :label="$t('feedback.id') || '反馈ID'">
            {{ selectedFeedback.id }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('feedback.username') || '用户名'">
            {{ selectedFeedback.user?.username || '未知用户' }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('feedback.email') || '用户邮箱'">
            {{ selectedFeedback.user?.email || '-' }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('feedback.type') || '反馈类型'">
            <el-tag :type="getFeedbackTypeColor(selectedFeedback.feedback_type)" style="display: flex; align-items: center; width: fit-content;">
              <el-icon style="margin-right: 4px;">
                <component :is="getFeedbackTypeIcon(selectedFeedback.feedback_type)" />
              </el-icon>
              {{ getFeedbackTypeText(selectedFeedback.feedback_type) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item :label="$t('feedback.contact') || '联系方式'">
            {{ selectedFeedback.contact_info || '-' }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('feedback.status') || '处理状态'">
            <el-tag :type="getStatusColor(selectedFeedback.status)">
              {{ getStatusText(selectedFeedback.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item :label="$t('feedback.createdAt') || '提交时间'">
            {{ formatDateTime(selectedFeedback.created_at) }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('feedback.updatedAt') || '更新时间'">
            {{ formatDateTime(selectedFeedback.updated_at) }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('feedback.processor') || '处理人'" v-if="selectedFeedback.processor">
            {{ selectedFeedback.processor.username }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('feedback.processedAt') || '处理时间'" v-if="selectedFeedback.processed_at">
            {{ formatDateTime(selectedFeedback.processed_at) }}
          </el-descriptions-item>
        </el-descriptions>
        
        <div v-if="selectedFeedback.process_note" class="process-note">
          <h4>{{ $t('feedback.processNote') || '处理说明：' }}</h4>
          <div class="note-text">
            {{ selectedFeedback.process_note }}
          </div>
        </div>
        
        <div class="feedback-content-detail">
          <h4>{{ $t('feedback.content') || '反馈内容：' }}</h4>
          <div class="content-text">
            {{ selectedFeedback.content }}
          </div>
        </div>
        
        <!-- 附件区域 -->
        <div v-if="hasAttachments(selectedFeedback)" class="attachments-section">
          <!-- 图片附件 -->
          <div v-if="selectedFeedback.attached_images && selectedFeedback.attached_images.length > 0" class="attachment-group">
            <h4>
              <el-icon><Picture /></el-icon>
              图片附件 ({{ selectedFeedback.attached_images.length }})
            </h4>
            <div class="image-grid">
              <div 
                v-for="(image, index) in selectedFeedback.attached_images" 
                :key="index"
                class="image-item"
                @click="previewImage(image)"
              >
                <img :src="getImageThumbnail(image)" :alt="image.filename" />
                <div class="image-info">
                  <span class="filename">{{ image.filename }}</span>
                  <span class="filesize">{{ formatFileSize(image.size) }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 文件附件 -->
          <div v-if="selectedFeedback.attached_files && selectedFeedback.attached_files.length > 0" class="attachment-group">
            <h4>
              <el-icon><Document /></el-icon>
              文件附件 ({{ selectedFeedback.attached_files.length }})
            </h4>
            <div class="file-list">
              <div 
                v-for="(file, index) in selectedFeedback.attached_files" 
                :key="index"
                class="file-item"
              >
                <div class="file-icon">
                  <el-icon><Document /></el-icon>
                </div>
                <div class="file-info">
                  <span class="filename">{{ file.filename }}</span>
                  <span class="filesize">{{ formatFileSize(file.size) }}</span>
                </div>
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="downloadFile(file)"
                  :loading="downloadingFiles[file.object_name]"
                >
                  <el-icon><Download /></el-icon>
                  下载
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
    
    <!-- 图片预览对话框 -->
    <el-dialog
      v-model="imagePreviewVisible"
      title="图片预览"
      width="800px"
      @close="handleCloseImagePreview"
      class="image-preview-dialog"
      align-center
    >
      <div v-loading="imagePreviewLoading" class="preview-image-container">
        <img v-if="previewImageUrl" :src="previewImageUrl" alt="预览图片" class="preview-image" />
      </div>
      <template #footer>
        <el-button type="primary" @click="downloadCurrentImage">
          <el-icon><Download /></el-icon>
          下载图片
        </el-button>
      </template>
    </el-dialog>

    <!-- 编辑反馈对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      :title="$t('feedback.edit') || '编辑反馈'"
      :width="isMobile ? '95%' : '50%'"
      :fullscreen="isMobile"
      :before-close="handleCloseEdit"
    >
      <el-form
        ref="editFormRef"
        :model="editForm"
        label-width="100px"
        :rules="editRules"
      >
        <el-form-item :label="$t('feedback.status') || '处理状态'" prop="status">
          <el-select v-model="editForm.status" placeholder="请选择处理状态">
            <el-option label="待处理" value="pending" />
            <el-option label="处理中" value="processing" />
            <el-option label="已完成" value="completed" />
            <el-option label="已关闭" value="closed" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('feedback.processNote') || '处理说明'" prop="process_note">
          <el-input
            v-model="editForm.process_note"
            type="textarea"
            :rows="4"
            placeholder="请输入处理说明"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="handleCloseEdit">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="handleSaveEdit" :loading="editLoading">
            {{ $t('common.ok') }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { Search, Refresh, HelpFilled, Warning, More, Download, Filter, Edit, View, Picture, Document } from '@element-plus/icons-vue'
import * as feedbackApi from '@/api/feedback'
import request from '@/utils/request'

// 开发环境标识
const isDev = import.meta.env.DEV

// i18n
const { t } = useI18n()

// 路由
const router = useRouter()
const route = useRoute()

// 移动端检测
const isMobile = ref(false)
const filtersCollapsed = ref(true)
const showMobileCards = ref(false)

// 数据状态
const loading = ref(false)
const feedbackList = ref([])
const detailVisible = ref(false)
const selectedFeedback = ref(null)

// 图片预览相关
const imagePreviewVisible = ref(false)
const imagePreviewLoading = ref(false)
const previewImageUrl = ref('')
const currentPreviewImage = ref(null)

// 文件下载状态
const downloadingFiles = ref({})

// 编辑相关
const editDialogVisible = ref(false)
const editLoading = ref(false)
const editFormRef = ref(null)
const editForm = reactive({
  id: null,
  status: '',
  process_note: ''
})

// 编辑表单验证规则
const editRules = {
  status: [
    { required: true, message: '请选择处理状态', trigger: 'change' }
  ]
}

// 搜索表单
const searchForm = reactive({
  search: '',
  feedback_type: '',
  status: '',
  dateRange: null
})

// 导出相关状态
const exportLoading = ref(false)

// 分页信息 - 从路由参数初始化
const pagination = reactive({
  page: parseInt(route.query.page) || 1,
  size: parseInt(route.query.pageSize) || 20,
  total: 0
})

// 获取反馈列表
const getFeedbackList = async () => {
  loading.value = true
  try {
    const params = {
      skip: (pagination.page - 1) * pagination.size,
      limit: pagination.size,
      search: searchForm.search,
      feedback_type: searchForm.feedback_type,
      status: searchForm.status
    }
    
    // 处理时间范围参数
    if (searchForm.dateRange && searchForm.dateRange.length === 2) {
      params.start_time = searchForm.dateRange[0]
      params.end_time = searchForm.dateRange[1]
    }
    
    // 清除空值参数
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) {
        delete params[key]
      }
    })

    const response = await feedbackApi.getAdminFeedback(params);

    if (response && response.data) {
      // 标准格式 {total: number, data: array}
      if (typeof response.data.total === 'number' && Array.isArray(response.data.data)) {
        feedbackList.value = response.data.data;
        pagination.total = response.data.total;
      }
      // 直接返回数组格式（尝试从响应头获取总数）
      else if (Array.isArray(response.data)) {
        feedbackList.value = response.data;
        // 尝试从响应对象或响应头中获取总数
        if (response.total !== undefined) {
          pagination.total = response.total;
        } else if (response.headers && response.headers['x-total-count']) {
          pagination.total = parseInt(response.headers['x-total-count']);
        } else {
          // 警告：无法获取总记录数，使用当前页数据长度（这是不准确的）
          console.warn('API响应中缺少total字段，分页总数可能不准确');
          pagination.total = response.data.length;
        }
      }
      else {
        ElMessage.error('API响应数据格式不正确');
        feedbackList.value = [];
        pagination.total = 0;
      }
    } else {
      feedbackList.value = [];
      pagination.total = 0;
    }

  } catch (error) {
    console.error('Failed to fetch feedback list:', error);
    
    // 根据错误类型提供更具体的错误信息
    if (error.response?.status === 403) {
      ElMessage.error('Permission denied')
    } else if (error.response?.status === 401) {
      ElMessage.error('登录已过期，请重新登录')
    } else if (error.response?.status >= 500) {
      ElMessage.error('服务器错误，请稍后重试')
    } else {
      ElMessage.error(`获取反馈列表失败: ${error.message}`)
    }
  } finally {
    loading.value = false
  }
}

// 搜索处理
const handleSearch = () => {
  router.replace({
    query: { page: 1, pageSize: pagination.size }
  })
}

// 重置搜索
const handleReset = () => {
  searchForm.search = ''
  searchForm.feedback_type = ''
  searchForm.status = ''
  searchForm.dateRange = null
  router.replace({
    query: { page: 1, pageSize: pagination.size }
  })
}

// 分页处理
const handleSizeChange = (val) => {
  router.replace({
    query: { page: 1, pageSize: val }
  })
}

const handleCurrentChange = (val) => {
  router.replace({
    query: { ...route.query, page: val, pageSize: pagination.size }
  })
}

// 排序处理
const handleSortChange = ({ prop, order }) => {
  // 这里可以添加排序逻辑
}

// 查看详情
const handleViewDetail = (row) => {
  selectedFeedback.value = row
  detailVisible.value = true
  
  // 预加载所有图片的URL
  if (row.attached_images && row.attached_images.length > 0) {
    row.attached_images.forEach(image => {
      if (!imageThumbnailCache.value[image.object_name]) {
        loadImageThumbnail(image)
      }
    })
  }
}

// Open detail dialog when Feishu card deep-link provides ?id=
const tryOpenFeedbackFromQuery = async () => {
  const rawId = route.query.id
  if (rawId === undefined || rawId === null || rawId === '') return

  const feedbackId = parseInt(String(Array.isArray(rawId) ? rawId[0] : rawId), 10)
  if (!Number.isFinite(feedbackId) || feedbackId <= 0) return

  if (detailVisible.value && selectedFeedback.value?.id === feedbackId) return

  let found = feedbackList.value.find((item) => Number(item.id) === feedbackId)
  if (!found) {
    try {
      const response = await feedbackApi.getAdminFeedback({
        id: feedbackId,
        skip: 0,
        limit: 1
      })
      let list = []
      if (response && response.data) {
        if (Array.isArray(response.data.data)) {
          list = response.data.data
        } else if (Array.isArray(response.data)) {
          list = response.data
        }
      }
      found = list.find((item) => Number(item.id) === feedbackId) || list[0] || null
    } catch (error) {
      console.warn('Failed to load feedback by id from query:', error)
    }
  }

  if (found) {
    handleViewDetail(found)
  } else {
    ElMessage.warning(`Feedback #${feedbackId} not found`)
  }
}

// 关闭详情对话框
const handleCloseDetail = () => {
  detailVisible.value = false
  selectedFeedback.value = null
}

// 编辑反馈
const handleEdit = (row) => {
  editForm.id = row.id
  editForm.status = row.status || 'pending'
  editForm.process_note = row.process_note || ''
  editDialogVisible.value = true
}

// 关闭编辑对话框
const handleCloseEdit = () => {
  editDialogVisible.value = false
  // 重置表单
  editForm.id = null
  editForm.status = ''
  editForm.process_note = ''
  if (editFormRef.value) {
    editFormRef.value.clearValidate()
  }
}

// 保存编辑
const handleSaveEdit = async () => {
  if (!editFormRef.value) return
  
  try {
    await editFormRef.value.validate()
    editLoading.value = true
    
    await feedbackApi.updateFeedback(editForm.id, {
      status: editForm.status,
      process_note: editForm.process_note
    })
    
    ElMessage.success('反馈更新成功')
    handleCloseEdit()
    // 刷新列表
    await getFeedbackList()
  } catch (error) {
    console.error('更新反馈失败:', error)
    if (error.response?.status === 403) {
      ElMessage.error('Permission denied')
    } else if (error.response?.status === 404) {
      ElMessage.error('反馈记录不存在')
    } else {
      ElMessage.error(`更新反馈失败: ${error.message}`)
    }
  } finally {
    editLoading.value = false
  }
}

// 获取反馈类型颜色
const getFeedbackTypeColor = (type) => {
  const colorMap = {
    'requirement_suggestion': 'primary',
    'function_exception': 'danger',
    'complaint': 'danger',
    'other': 'info'
  }
  return colorMap[type] || 'info'
}

// 获取反馈类型文本
const getFeedbackTypeText = (type) => {
  const keyMap = {
    'requirement_suggestion': 'feedback.typeRequirement',
    'function_exception': 'feedback.typeException',
    'complaint': 'feedback.options.complaint',
    'other': 'feedback.typeOther'
  }
  const key = keyMap[type] || 'feedback.typeOther'
  return t(key)
}

// 获取反馈类型图标
const getFeedbackTypeIcon = (type) => {
  const iconMap = {
    'requirement_suggestion': 'HelpFilled',
    'function_exception': 'Warning',
    'other': 'More'
  }
  return iconMap[type] || 'More'
}

// 获取状态颜色
const getStatusColor = (status) => {
  // 处理null值，默认为pending
  const normalizedStatus = status || 'pending'
  const colorMap = {
    'pending': 'warning',
    'processing': 'primary',
    'completed': 'success',
    'closed': 'info'
  }
  return colorMap[normalizedStatus] || 'info'
}

// 获取状态文本
const getStatusText = (status) => {
  const normalizedStatus = status || 'pending'
  const keyMap = {
    'pending': 'feedback.statusPending',
    'processing': 'feedback.statusProcessing',
    'completed': 'feedback.statusCompleted',
    'closed': 'feedback.statusClosed'
  }
  const key = keyMap[normalizedStatus] || 'feedback.statusPending'
  return t(key)
}

// 格式化日期时间
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  return new Date(dateTime).toLocaleString('zh-CN')
}

// 导出CSV功能
const handleExport = async () => {
  exportLoading.value = true
  try {
    const params = {
      search: searchForm.search,
      feedback_type: searchForm.feedback_type,
      status: searchForm.status
    }
    
    // 处理时间范围参数
    if (searchForm.dateRange && searchForm.dateRange.length === 2) {
      params.start_time = searchForm.dateRange[0]
      params.end_time = searchForm.dateRange[1]
    }
    
    // 清除空值参数
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) {
        delete params[key]
      }
    })

    const response = await feedbackApi.exportFeedback(params)
    
    // 处理响应数据，确保是Blob对象
    let blob
    let filename = null
    
    if (response instanceof Blob) {
      // 如果响应拦截器返回的是Blob对象
      blob = response
    } else if (response.data instanceof Blob) {
      // 如果响应拦截器返回的是完整响应对象
      blob = response.data
      
      // 尝试从响应头获取文件名
      const contentDisposition = response.headers?.['content-disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/)
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '') // 去除引号
        }
      }
    } else {
      // 如果响应数据是其他格式，尝试创建Blob
      const data = response.data || response
      if (typeof data === 'string') {
        blob = new Blob([data], { type: 'text/csv;charset=utf-8' })
      } else {
        // 如果数据不是字符串，可能是错误响应
        console.error('导出数据格式异常:', data)
        throw new Error('导出数据格式异常')
      }
    }
    
    // 检查Blob是否有效
    if (!blob || blob.size === 0) {
      throw new Error('导出的文件为空，请检查是否有数据')
    }
    
    console.log('导出文件信息:', {
      size: blob.size,
      type: blob.type,
      filename: filename
    })
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // 使用服务器返回的文件名，如果没有则生成默认文件名
    if (!filename) {
      const now = new Date()
      const timestamp = now.toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_')
      filename = `feedback_export_${timestamp}.csv`
    }
    link.download = filename
    
    // 触发下载
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
  } catch (error) {
    console.error('导出失败:', error)
    if (error.response?.status === 403) {
      ElMessage.error('Permission denied')
    } else {
      ElMessage.error(`导出失败: ${error.message}`)
    }
  } finally {
    exportLoading.value = false
  }
}

// 检查是否有附件
const hasAttachments = (feedback) => {
  return (feedback.attached_images && feedback.attached_images.length > 0) ||
         (feedback.attached_files && feedback.attached_files.length > 0)
}

// 格式化文件大小
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

// 缓存图片URL
const imageThumbnailCache = ref({})

// 获取图片缩略图（同步返回，如果没有缓存则返回占位符）
const getImageThumbnail = (image) => {
  // 如果已经缓存，直接返回
  if (imageThumbnailCache.value[image.object_name]) {
    return imageThumbnailCache.value[image.object_name]
  }
  
  // 异步加载图片URL（不等待）
  loadImageThumbnail(image)
  
  // 返回占位符
  return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999"%3E加载中...%3C/text%3E%3C/svg%3E'
}

// 异步加载图片URL
const loadImageThumbnail = async (image) => {
  try {
    const response = await feedbackApi.getFilePresignedUrl(image.object_name)
    console.log('加载图片缩略图成功:', response)
    
    // 如果返回的是代理URL，通过fetch获取blob再转换为blob URL
    let imageUrl = response.url
    if (imageUrl.startsWith('/api/')) {
      try {
        // 使用request工具发送带认证的请求
        const imageResponse = await request({
          url: imageUrl,
          method: 'get',
          responseType: 'blob'
        })
        
        // 将blob转换为blob URL
        const blobUrl = URL.createObjectURL(imageResponse)
        imageThumbnailCache.value[image.object_name] = blobUrl
        console.log('图片blob URL生成成功:', blobUrl)
      } catch (fetchError) {
        console.error('获取图片blob失败:', fetchError)
        throw fetchError
      }
    } else {
      // 公网URL直接使用
      imageThumbnailCache.value[image.object_name] = imageUrl
    }
  } catch (error) {
    console.error('加载图片缩略图失败:', error)
    imageThumbnailCache.value[image.object_name] = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23f56c6c"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="12" fill="white"%3E加载失败%3C/text%3E%3C/svg%3E'
  }
}

// 预览图片
const previewImage = async (image) => {
  currentPreviewImage.value = image
  imagePreviewVisible.value = true
  imagePreviewLoading.value = true
  previewImageUrl.value = ''
  
  try {
    const response = await feedbackApi.getFilePresignedUrl(image.object_name)
    const imageUrl = response.url
    
    // 如果是代理URL，通过fetch获取blob
    if (imageUrl.startsWith('/api/')) {
      const imageResponse = await request({
        url: imageUrl,
        method: 'get',
        responseType: 'blob'
      })
      previewImageUrl.value = URL.createObjectURL(imageResponse)
    } else {
      previewImageUrl.value = imageUrl
    }
  } catch (error) {
    console.error('获取图片URL失败:', error)
    ElMessage.error('获取图片失败，请稍后重试')
    imagePreviewVisible.value = false
  } finally {
    imagePreviewLoading.value = false
  }
}

// 关闭图片预览
const handleCloseImagePreview = () => {
  imagePreviewVisible.value = false
  previewImageUrl.value = ''
  currentPreviewImage.value = null
}

// 下载当前预览的图片
const downloadCurrentImage = () => {
  if (previewImageUrl.value && currentPreviewImage.value) {
    const link = document.createElement('a')
    link.href = previewImageUrl.value
    link.download = currentPreviewImage.value.filename
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    ElMessage.success('图片下载已开始')
  }
}

// 下载文件
const downloadFile = async (file) => {
  try {
    downloadingFiles.value[file.object_name] = true
    
    const response = await feedbackApi.getFilePresignedUrl(file.object_name)
    const fileUrl = response.url
    
    // 如果是代理URL，通过fetch获取blob
    if (fileUrl.startsWith('/api/')) {
      const fileResponse = await request({
        url: fileUrl,
        method: 'get',
        responseType: 'blob'
      })
      
      // 创建blob URL并触发下载
      const blobUrl = URL.createObjectURL(fileResponse)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = file.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } else {
      // 公网URL直接下载
      const link = document.createElement('a')
      link.href = fileUrl
      link.download = file.filename
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
    
    ElMessage.success('文件下载已开始')
  } catch (error) {
    console.error('下载文件失败:', error)
    ElMessage.error('文件下载失败，请稍后重试')
  } finally {
    downloadingFiles.value[file.object_name] = false
  }
}

// 移动端检测函数
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
  showMobileCards.value = isMobile.value
  if (isMobile.value && !filtersCollapsed.value) {
    filtersCollapsed.value = true
  }
}

// 切换搜索条件显示
const toggleFilters = () => {
  filtersCollapsed.value = !filtersCollapsed.value
}

// 切换移动端显示模式
const toggleMobileView = () => {
  showMobileCards.value = !showMobileCards.value
}

// 组件挂载时获取数据
// 监听路由参数变化
watch(
  () => route.query,
  async (newQuery) => {
    const newPage = parseInt(newQuery.page) || 1
    const newPageSize = parseInt(newQuery.pageSize) || 20
    
    if (pagination.page !== newPage) {
      pagination.page = newPage
    }
    if (pagination.size !== newPageSize) {
      pagination.size = newPageSize
    }
    
    await getFeedbackList()
    await tryOpenFeedbackFromQuery()
  }
)

onMounted(async () => {
  await getFeedbackList()
  await tryOpenFeedbackFromQuery()
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
})
</script>

<style scoped>
.feedback-management-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 22px;
  color: #303133;
}

.search-bar {
  margin-bottom: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

.search-form {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: end;
}

.search-form .el-form-item {
  flex-shrink: 0;
}

.search-form .el-form-item {
  margin-bottom: 0;
}

.feedback-content {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 表格样式优化 */
:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);
}

:deep(.el-table th) {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #606266;
}

.feedback-detail {
  padding: 20px 0;
}

.feedback-content-detail {
  margin-top: 20px;
}

.feedback-content-detail h4 {
  margin-bottom: 10px;
  color: #303133;
}

.content-text {
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.process-note {
  margin-top: 20px;
}

.process-note h4 {
  margin-bottom: 10px;
  color: #303133;
}

.note-text {
  padding: 15px;
  background-color: #e8f5e8;
  border-radius: 4px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  border-left: 4px solid #67c23a;
}

/* 附件区域样式 */
.attachments-section {
  margin-top: 20px;
  padding: 20px;
  background-color: #fafbfc;
  border-radius: 8px;
  border: 1px solid #e1e4e8;
}

.attachment-group {
  margin-bottom: 24px;
}

.attachment-group:last-child {
  margin-bottom: 0;
}

.attachment-group h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}

.attachment-group h4 .el-icon {
  font-size: 18px;
  color: #409eff;
}

/* 图片网格 */
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.image-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e1e4e8;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.image-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.image-item img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
}

.image-info {
  padding: 8px;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.image-info .filename {
  font-size: 12px;
  color: #303133;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-info .filesize {
  font-size: 11px;
  color: #909399;
}

/* 文件列表 */
.file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
  transition: all 0.2s ease;
}

.file-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.file-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f9ff;
  border-radius: 6px;
  margin-right: 12px;
  flex-shrink: 0;
}

.file-icon .el-icon {
  font-size: 20px;
  color: #409eff;
}

.file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.file-info .filename {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-info .filesize {
  font-size: 12px;
  color: #909399;
}

/* 图片预览对话框 */
.image-preview-dialog :deep(.el-dialog__body) {
  padding: 20px;
}

.preview-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  max-height: 70vh;
  overflow: auto;
  background: #f5f7fa;
  border-radius: 8px;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

/* 页面头部移动端样式 */
.header-left {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mobile-controls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* 移动端搜索表单样式 */
.mobile-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.mobile-form .el-form-item {
  margin-bottom: 0;
}

.mobile-form .el-form-item__label {
  width: auto !important;
  margin-bottom: 5px;
}

.mobile-form .el-form-item__content {
  margin-left: 0 !important;
}

/* 移动端卡片容器样式 */
.mobile-cards-container {
  margin-bottom: 20px;
}

.cards-loading-wrapper {
  min-height: 200px;
}

/* 反馈卡片样式 */
.feedback-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #ebeef5;
  transition: all 0.3s ease;
}

.feedback-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f5;
}

.feedback-info h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.feedback-info .email {
  margin: 0 0 4px 0;
  font-size: 13px;
  color: #909399;
}

.feedback-info .feedback-id {
  font-size: 12px;
  color: #c0c4cc;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.status-type {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.card-content {
  margin-bottom: 16px;
}

/* 反馈内容区域 */
.feedback-content-section {
  margin-bottom: 16px;
}

.feedback-content-section h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #303133;
  font-weight: 600;
}

.content-preview {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #409EFF;
  font-size: 13px;
  line-height: 1.5;
  color: #606266;
  max-height: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* 元数据区域 */
.feedback-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #fafafa;
  border-radius: 6px;
}

.meta-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.meta-item .label {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

.meta-item .value {
  font-size: 12px;
  color: #606266;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* 卡片操作按钮样式 */
.card-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #f0f2f5;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .feedback-management-container {
    padding: 10px;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .search-form {
    width: 100%;
  }
  
  .pagination {
    justify-content: center;
  }

  /* 移动端分页样式优化 */
  :deep(.el-pagination) {
    flex-wrap: wrap;
    justify-content: center;
  }

  :deep(.el-pagination .el-pager) {
    flex-wrap: wrap;
  }

  /* 移动端表格优化 */
  :deep(.el-table) {
    font-size: 12px;
  }

  :deep(.el-table th) {
    padding: 8px 4px;
  }

  :deep(.el-table td) {
    padding: 8px 4px;
  }

  /* 移动端对话框优化 */
  :deep(.el-dialog) {
    margin: 0 !important;
  }

  :deep(.el-dialog__body) {
    padding: 15px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }

  /* 移动端描述列表优化 */
  :deep(.el-descriptions__body .el-descriptions__table) {
    table-layout: auto;
  }

  :deep(.el-descriptions__label) {
    width: 35% !important;
    font-size: 13px;
  }

  :deep(.el-descriptions__content) {
    width: 65% !important;
    font-size: 13px;
  }
}
</style> 